import { defineStore, storeToRefs } from 'pinia'
import type { Undefinable } from 'ts-helpers'
import { ref, watch } from 'vue'
import {
  type Compilation,
  CompilationAdapter,
  type CompilationDTO,
  useCompilation
} from '@/5_entities/Compilation'
import type { Event } from '@/5_entities/Event'
import { useLocation } from '@/5_entities/Location'
import type { Venue } from '@/5_entities/Venue'
import { usePageLoading } from '@/6_shared/composables'
import type { SearchResult } from '@/6_shared/model'
import { homeService } from '../api'
import type { HomeFreeEventsQuery } from '../model'

export const useHome = defineStore('home', () => {
  const addEventsInSet = (
    items: SearchResult<'event' | 'venue', Event | Venue>['items'],
    visibleEvents: number
  ) => {
    items.forEach((item, index) => {
      if (index + 1 > visibleEvents) return

      item.type === 'event' && eventsSet.add(item.object.id)
    })
  }
  const sortByImage = (items: SearchResult<'event' | 'venue', Event | Venue>['items']) => {
    items.sort(({ object: itemA }, { object: itemB }) => {
      if (itemA.image && !itemB.image) return -1
      if (!itemA.image && itemB.image) return 1

      return 0
    })
  }

  const getHomePageCompilations = async (pageSize: number, visibleEvents: number) => {
    const { getPageCompilations } = useCompilation()

    await getPageCompilations('homePage', 'homepage', pageSize)
    const { pageCompilations } = useCompilation()
    pageCompilations.forEach((compilation) => {
      sortByImage(compilation?.items ?? [])
      addEventsInSet(compilation?.items ?? [], visibleEvents)
    })
  }

  //free
  const { initialized: homeFreeEventsInitialized, load: loadHomeFreeEvents } = usePageLoading()
  const FREE_EVENTS_COUNT = 64
  const freeEvents = ref<Undefinable<Compilation>>(undefined)
  const cleanFreeEvents = () => {
    homeFreeEventsInitialized.value = false
    freeEvents.value = undefined
  }
  const setFreeEvents = (data: CompilationDTO) => {
    freeEvents.value = new CompilationAdapter(data)
    freeEvents.value && sortByImage(freeEvents.value.items)
  }

  const eventsSet = new Set<number>()
  const { currentLocation } = storeToRefs(useLocation())
  watch(
    () => currentLocation.value,
    (val, oldVal) => {
      if (val?.id !== oldVal?.id) {
        eventsSet.clear()
      }
    }
  )

  const getFreeEvents = async () => {
    loadHomeFreeEvents(
      async () => {
        const filter: HomeFreeEventsQuery = {
          pagination: {
            pageSize: FREE_EVENTS_COUNT,
            currentPage: 1
          },
          filter: {
            excludeIds: Array.from(eventsSet)
          }
        }

        const freeEventsCompilation = await homeService.getFreeEvents(filter)

        setFreeEvents(freeEventsCompilation[0])
      },
      (error: any) => {
        console.error('failed to load home free events ', error)
      }
    )
  }

  return {
    getHomePageCompilations,
    freeEvents,
    cleanFreeEvents,
    getFreeEvents,
    homeFreeEventsInitialized
  }
})
